/**
 * Off-thread ресайз слотов через Web Worker. Инстансом владеет `RvfcSkipOptimizer`
 * (создаёт через `createWorkerPreScale`) и зовёт `beforeDraw` / `acquireSlot` из своего `drawSlot`.
 *
 * Реконсилирует набор треков из streamToVideosMap: новые клонирует
 * (`track.clone()` — оригинал остаётся на <video> для Goloom), оборачивает в
 * MediaStreamTrackProcessor и переносит `.readable` в worker. Хранит последний
 * слот-битмап на `(trackId, ключ-размера)`; `acquireSlot` отдаёт готовый битмап
 * (resize уже сделан в worker'е).
 *
 * Корректность под churn: ключ — trackId (не позиция); epoch на трек отбрасывает
 * поздние битмапы после remove/re-register; teardown останавливает клоны и
 * закрывает битмапы. Feature-detect + fallback: нет MSTP/Worker/OffscreenCanvas —
 * `createWorkerPreScale` возвращает null, и `RvfcSkipOptimizer` рисует синхронным кэшем;
 * о факте «не включился» пишем `logger.event` для наблюдаемости на проде.
 *
 * Включается отдельным флагом — селектором `selectIsWorkerPreScaleEnabled`.
 */
import { Area } from '../../../../../../shared/helpers/layout-manager/types';
import logError from '../../../../../../shared/helpers/log-error';
import { createLogger } from '../../../../../../shared/helpers/loggerFacade';
import { StreamToVideosMap } from '../../../../types';
import { DisplayMode } from '../draw-helpers';
import { detectWorkerSupport, getVideoTrack, makeSlotKey } from './helpers';
import { MainToWorkerMessage, WorkerToMainMessage } from './types';

const MAX_KEYS_PER_TRACK = 3;
const PRESCALE_UNAVAILABLE_EVENT = 'room_agent_worker_prescale_unavailable';
const PRESCALE_ENABLED_EVENT = 'room_agent_worker_prescale_enabled';

const logger = createLogger('room-agent-prescale');

interface TrackEntry {
    trackId: string;
    epoch: number;
    clone: MediaStreamTrack;
    requestedKeys: Set<string>;
    latest: Map<string, ImageBitmap>;
}

export class WorkerPreScaleOptimizer {
    private readonly _worker: Worker;
    private readonly _registry: Map<string, TrackEntry> = new Map();
    private _epochCounter = 0;
    private _wasRegisterErrorLogged = false;

    constructor(worker: Worker) {
        this._worker = worker;
        this._worker.onmessage = (event: MessageEvent<WorkerToMainMessage>) => this._onMessage(event.data);
    }

    /** Реконсиляция реестра под текущий состав треков. Вызывается из drawer каждый кадр. */
    public beforeDraw(streamToVideosMap: StreamToVideosMap): void {
        const current = new Set<string>();
        for (const track of streamToVideosMap.byTrack.keys()) {
            current.add(track.id);
            if (!this._registry.has(track.id)) {
                this._register(track);
            }
        }
        for (const trackId of [...this._registry.keys()]) {
            if (!current.has(trackId)) {
                this._unregister(trackId);
            }
        }
    }

    /** Возвращает готовый слот-битмап или null (тогда drawer рисует прежним путём). */
    public acquireSlot(video: HTMLVideoElement, drawArea: Area, mode: DisplayMode): ImageBitmap | null {
        const track = getVideoTrack(video);
        const entry = track ? this._registry.get(track.id) : undefined;
        if (!entry) {
            return null;
        }

        const key = makeSlotKey(drawArea.width, drawArea.height, mode);
        if (entry.requestedKeys.has(key)) {
            // LRU: помечаем размер недавно использованным, чтобы FIFO не вытеснял активный.
            entry.requestedKeys.delete(key);
            entry.requestedKeys.add(key);
        } else {
            this._requestSize(entry, key, drawArea, mode);
        }

        // Только точный размер: чужой растянулся бы под другой aspect ratio. Нет точного —
        // возвращаем null, drawer честно дорисует синхронным путём (без искажений).
        return entry.latest.get(key) ?? null;
    }

    public destroy(): void {
        for (const trackId of [...this._registry.keys()]) {
            this._unregister(trackId);
        }
        this._worker.terminate();
    }

    private _register(track: MediaStreamTrack): void {
        let clone: MediaStreamTrack | null = null;
        try {
            clone = track.clone();
            const processor = new MediaStreamTrackProcessor({ track: clone });
            const readable = processor.readable;
            const epoch = ++this._epochCounter;
            this._registry.set(track.id, {
                trackId: track.id,
                epoch,
                clone,
                requestedKeys: new Set(),
                latest: new Map(),
            });
            this._post({ type: 'register', trackId: track.id, epoch, readable }, [readable]);
        } catch (error) {
            // Если MediaStreamTrackProcessor бросил уже после clone() — клон осиротеет, останавливаем его.
            clone?.stop();
            if (!this._wasRegisterErrorLogged) {
                this._wasRegisterErrorLogged = true;
                logError({ block: 'recording', method: 'WorkerPreScaleOptimizer._register' }, error as Error);
            }
        }
    }

    private _unregister(trackId: string): void {
        const entry = this._registry.get(trackId);
        if (!entry) {
            return;
        }
        this._post({ type: 'unregister', trackId });
        try {
            entry.clone.stop();
        } catch {
            // best-effort
        }
        entry.latest.forEach((bitmap) => bitmap.close());
        this._registry.delete(trackId);
    }

    private _requestSize(entry: TrackEntry, key: string, drawArea: Area, mode: DisplayMode): void {
        // FIFO-вытеснение размеров, чтобы не копить slot-canvas'ы в worker'е при смене раскладки.
        if (entry.requestedKeys.size >= MAX_KEYS_PER_TRACK) {
            const oldestKey = entry.requestedKeys.values().next().value as string | undefined;
            if (oldestKey !== undefined) {
                entry.requestedKeys.delete(oldestKey);
                entry.latest.get(oldestKey)?.close();
                entry.latest.delete(oldestKey);
                this._post({ type: 'dropSize', trackId: entry.trackId, key: oldestKey });
            }
        }
        entry.requestedKeys.add(key);
        this._post({
            type: 'requestSize',
            trackId: entry.trackId,
            epoch: entry.epoch,
            key,
            slotWidth: Math.round(drawArea.width),
            slotHeight: Math.round(drawArea.height),
            mode,
        });
    }

    private _onMessage(message: WorkerToMainMessage): void {
        if (message.type === 'error') {
            // Поток кадров трека закончился/упал в воркере: роняем кэш трека (acquireSlot вернёт
            // null → drawer упадёт в синхронный кэш на живой <video>). 'stream-ended' — штатно, не ошибка.
            const failed = this._registry.get(message.trackId);
            failed?.latest.forEach((bitmap) => bitmap.close());
            failed?.latest.clear();
            if (message.message !== 'stream-ended') {
                logError(
                    { block: 'recording', method: 'WorkerPreScaleOptimizer.pump' },
                    new Error(`prescale worker: ${message.message}`)
                );
            }
            return;
        }
        if (message.type !== 'slot') {
            return;
        }
        const entry = this._registry.get(message.trackId);
        // Отбрасываем битмап, если трек удалён/перерегистрирован (epoch) ИЛИ размер уже не запрошен
        // (гонка с dropSize/FIFO — иначе осиротевший битмап осядет в latest и утечёт).
        if (!entry || entry.epoch !== message.epoch || !entry.requestedKeys.has(message.key)) {
            message.bitmap.close();
            return;
        }
        entry.latest.get(message.key)?.close();
        entry.latest.set(message.key, message.bitmap);
    }

    private _post(message: MainToWorkerMessage, transfer?: Transferable[]): void {
        if (transfer) {
            this._worker.postMessage(message, transfer);
        } else {
            this._worker.postMessage(message);
        }
    }
}

/**
 * Создаёт worker-оптимизатор или возвращает null (нет нужных API / ошибка создания).
 * Строка воркера грузится лениво (`import()` → отдельный чанк), чтобы не попадать в общий
 * бандл клиентам без этой фичи. Benign-недоступность пишем событием (`logger.event`),
 * реальную ошибку — `logError`. Владелец инстанса — RvfcSkipOptimizer.
 */
export async function createWorkerPreScale(): Promise<WorkerPreScaleOptimizer | null> {
    const support = detectWorkerSupport();
    if (support !== 'ok') {
        logger.event(PRESCALE_UNAVAILABLE_EVENT, { value: support });
        return null;
    }

    // Воркеру нужен URL со скриптом. Отдельный файл воркера здесь не годится: статика
    // отдаётся с отдельного домена-CDN (классический воркер с чужого домена браузер не пускает),
    // и сборка файл воркера не создаёт. Поэтому код воркера лежит строкой и запускается из
    // blob:-URL — он принадлежит домену страницы (не кросс-домен) и разрешён нашим CSP.
    // Подробнее — prescale-worker-source.ts.
    let objectUrl: string | undefined;
    try {
        const { PRESCALE_WORKER_SOURCE } = await import('./prescale-worker-source');
        const blob = new Blob([PRESCALE_WORKER_SOURCE], { type: 'text/javascript' });
        objectUrl = URL.createObjectURL(blob);
        const worker = new Worker(objectUrl);
        logger.event(PRESCALE_ENABLED_EVENT);
        return new WorkerPreScaleOptimizer(worker);
    } catch (error) {
        logError({ block: 'recording', method: 'createWorkerPreScale' }, error as Error);
        return null;
    } finally {
        // Revoke ПОСЛЕ конструирования воркера (воркер уже держит ссылку на blob).
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
    }
}

/**
 * Прогрев lazy-чанка воркера: заранее тянет тот же модуль, что грузит `createWorkerPreScale`,
 * чтобы он осел в HTTP-кэше браузера. Зовётся на warmup-странице room-agent'а — warmup и реальные
 * звонки делят один браузер (TabManager), поэтому первая SIP-запись получит cache-hit вместо
 * сетевого round-trip и не словит просадку FPS на первых кадрах. Best-effort: ошибки глушим.
 */
export function warmUpWorkerPreScale(): void {
    import('./prescale-worker-source').catch(() => undefined);
}
